import { Injectable } from '@angular/core';
import { DirectoryEntry, Entry, File, FileEntry, Flags, IFile as CordovaFile } from '@ionic-native/file';
import { UUID } from 'angular2-uuid';
import { FileSystemHelper } from '../../../app/helpers/file-system.helper';
import { CordovaFileEntryInfo, CordovaFileHelper } from '../../../app/helpers/file-system/cordova-file.helper';
import { FileHelper, PathInfoType } from '../../../app/helpers/file-system/file.helper';
import { asyncForEach } from '../../../core/util/async-for-each';
import { File as FileModel } from '../../business-objects/file/file.model';

export interface IFileChunk {
    data: Blob;
    isLastChunk: boolean;
}

export enum BlobToPathStrategy {
    DESKTOP,
    MOBILE
}

@Injectable()
export class UniversalFilesystemService {

    constructor(protected fs: File) {
    }

    /**
     * This method take a blob and returns a "path".
     *
     * The path is a string that will either be an ObjectUrl or a path on the
     * browser's FileSystem. Which one we use is determined by the `strategy`
     * parameter.
     *
     * When the strategy is DESKTOP, we will use ObjectUrl because they don't
     * require us to copy any data into the browser's file system - the
     * ObjectUrl acts as a pointer to the file in memory.
     *
     * When the strategy is MOBILE, we have to copy the blob data from the
     * device's storage to the app's storage so we can generate a persistable
     * url to it. This isn't a particularly great solution, however we need to
     * make do with what we have until we can find better media picker.
     *
     * @todo
     *  we need something like "image-picker" but that will also support
     *  picking other media types such as video
     *
     * # ObjectUrl
     *
     * Using the ObjectUrl has the benefit of avoiding the browser's filesystem,
     * which is limited in size.
     *
     * However the downside is that an ObjectUrl is not persistent for longer
     * than the document object. Therefore if we exit the page, the ObjectUrl we
     * had is no longer valid. This means it's no good for mobile - where we
     * exist in a hostile environment, and loosing the ObjectUrl means we
     * wouldn't be able to resume our upload after an app restart.
     *
     * # FileSystem
     *
     * FileSystem urls will point to either the browser's file system, or if
     * we're on the mobile device directly to the storage.
     *
     * When running in a desktop browser, we avoid the use of the browser's file
     * system because it is limited in size.
     *
     * When running on a mobile device, we want to use file system because it
     * offers us persistent storage across app restarts, which means we can
     * resume our uploads.
     */
    async blobToPath(blob: Blob, strategy: BlobToPathStrategy, id = UUID.UUID()): Promise<string> {

        switch (strategy) {
            case BlobToPathStrategy.DESKTOP: {
                return FileSystemHelper.blobToObjectUrl(blob);
            }
            case BlobToPathStrategy.MOBILE: {
                const fileEntry = await this.newFileEntry(id, {create: true, exclusive: false});
                await CordovaFileHelper.write(fileEntry, blob);
                return fileEntry.toURL();
            }
            default: {
                throw Error('invalid BlobToPathStrategy');
            }
        }
    }

    async blobToBase64(blob: Blob): Promise<string> {
        return FileSystemHelper.blobToDataUrl(blob);
    }

    /**
     * Returns the blob from the given path.
     * This should be used when want access to the binary data of the file (for rendering, download etc)
     * We should pass in the `localFileSystemPath` from the FileModel
     */
    async pathToBlob(path: string): Promise<Blob> {

        const pathInfo = FileHelper.parsePath(path);
        let blob: Blob;

        switch (pathInfo.type) {
            case PathInfoType.FILESYSTEM: {
                const fileEntry = await this.pathToFileEntry(path);
                blob = await CordovaFileHelper.getFile(fileEntry);

                // if we are running in mobile context the blob that is returned
                // is actually an IFile. We need to convert it to Blob otherwise
                // we will receive an error if we append it to FormData
                if (pathInfo.cordova) {
                    blob = await CordovaFileHelper.cordovaFileToBlob(blob as CordovaFile);
                }
                break;
            }
            case PathInfoType.BLOB: {
                blob = await FileSystemHelper.objectUrlToBlob(path);
                break;
            }
            default: {
                throw new Error('invalid path type');
            }
        }

        return blob;
    }

    /**
     * Returns an Object url for the file located at path
     */
    async pathToObjectUrl(path: string): Promise<string> {
        const blob: Blob = await this.pathToBlob(path);
        return URL.createObjectURL(blob);
    }

    /**
     * Iterates over all files in the persistent store for the app and invokes the callback.
     * The callback should return a promise.
     */
    async forEachFileEntry(cb: (fileEntry: Entry, info: CordovaFileEntryInfo) => Promise<any>): Promise<void> {

        if (!this.fs.dataDirectory) {
            return;
        }

        // get a list of all file entries in the persistent storage folder
        let entries = await this.fs.listDir(this.fs.dataDirectory, '');

        // remove anything from the list that is not a file
        entries = entries.filter(entry => entry.isFile);

        await asyncForEach(entries, async entry => {

            // this is a little hack to fix the file system name on iOS
            if (entry.filesystem.name === undefined) {
                entry.filesystem.name = 'persistent';
            }

            await cb(entry, CordovaFileHelper.extractFilePathParts(entry.fullPath));
        });
    }

    async pathToFileEntry(path: string, options?: Flags): Promise<FileEntry> {
        const pathInfo = FileHelper.parsePath(path);

        if (pathInfo.type !== PathInfoType.FILESYSTEM) {
            throw Error('path is not a valid filesystem path');
        }

        const filePathParts = CordovaFileHelper.extractFilePathParts(path);
        const directoryEntry = await this.fs.resolveDirectoryUrl(filePathParts.directoryPath);

        return new Promise<FileEntry>((resolve, reject) => {
            directoryEntry.getFile(filePathParts.filename, options, resolve, reject);
        });
    }

    /**
     * Creates a new FileEntry with the given filename - which should be the
     * guid of the FileModel that it will hold data for.
     */
    private async newFileEntry(
        filename: string,
        options?: Flags,
        directory: string = this.fs.cacheDirectory
    ): Promise<FileEntry> {
        const directoryEntry: DirectoryEntry = await this.fs.resolveLocalFilesystemUrl(directory) as DirectoryEntry;
        return new Promise<FileEntry>((resolve, reject) => {
            directoryEntry.getFile(filename, options, resolve, reject);
        });
    }
}



// WEBPACK FOOTER //
// ./src/vas/services/universal-filesystem/universal-filesystem.service.ts